import clientAccount from 'account-client';
import type { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { clearAllAuthCookies } from '~/composables/useCookieManager';
import { performTokenRefresh } from '~/composables/useTokenRefresh';

// Define types for error response data
interface ValidationErrorData {
  errors?: object;
}

interface MfaErrorData {
  mfa_redirect?: boolean;
  url?: string;
}

interface ErrorResponseData {
  message?: string;
  Message?: string;
}

// Extend axios config to track retry attempts
interface RetryableAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

export default defineNuxtPlugin(nuxtApp => {
  const auth = useAuth();
  const clientsStore = useClientsStore();
  const validationStore = useValidationInputStore();
  const accessStore = useAccessStore();
  const runtimeConfig = useRuntimeConfig();
  const url = useRequestURL();

  const client = clientAccount({
    baseURL: runtimeConfig.public.accountsApiUrl,
  });

  client.axios.interceptors.request.use(config => {
    config.headers.ClientId =
      runtimeConfig.public.clientId || clientsStore.selectedClientId;
    config.headers.Authorization = auth.strategy.token.get();

    validationStore.resetFields();

    return config;
  });

  client.axios.interceptors.response.use(
    (response: object) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as RetryableAxiosRequestConfig;

      if (
        error.response?.status === 401 &&
        originalRequest &&
        !originalRequest._retry &&
        !originalRequest.url?.includes('/oauth/token') &&
        !originalRequest.url?.includes('/oauth/refresh')
      ) {
        originalRequest._retry = true;

        try {
          // Use the shared performTokenRefresh to prevent race conditions
          const refreshSuccess = await performTokenRefresh();

          if (!refreshSuccess) {
            throw new Error('Token refresh failed');
          }

          originalRequest.headers.Authorization = auth.strategy.token.get();
          return client.axios(originalRequest);
        } catch {
          clearAllAuthCookies();
          await auth.logout();
          return Promise.reject(error);
        }
      }

      if (
        error.response?.status === 422 &&
        typeof error.response?.data === 'object' &&
        error.response.data !== null
      ) {
        const validationData = error.response.data as ValidationErrorData;
        if (validationData.errors) {
          validationStore.setInvalidFields(validationData.errors);
        }
      }

      if (error?.response?.status === 403) {
        const mfaData = error.response.data as MfaErrorData;
        if (mfaData?.mfa_redirect) {
          return navigateTo(`${mfaData.url}?return_to=${encodeURI(url.href)}`, {
            external: true,
          });
        }

        // Extract error message from response or use fallback
        const errorData = error.response.data as ErrorResponseData;
        const errorMessage =
          errorData?.message ||
          errorData?.Message ||
          nuxtApp.$i18n.t('access_denied.resource_message');

        // A denied read means the content cannot be shown, so cover it. A denied
        // action means the caller reports it — blanking the whole app would trap
        // the user, since only a route change clears the overlay.
        if (error.config?.method?.toLowerCase() === 'get') {
          accessStore.setPartialAccessDenied(true, errorMessage);
        }
      }

      return Promise.reject(error);
    }
  );

  nuxtApp.provide('clientAccount', client);
});
